import styled from 'styled-components';

import NotificationStyled from '../../../../../components/Notification';
import StickyNav from '../../../../../components/StickyNav';
import { IconInfoCircle as InfoCircle } from '../../../../../core';
import UseCasePalettes from '../../../components/UseCasePalettes';

interface Props {
  className?: string;
}

const UseCaseTab = ({ className = '' }: Props) => (
  <div className={className}>
    <div className="tab-content">
      <div className="definition">
        <h2 className="title" id="definition">
          Definition
        </h2>
        <p>
          A horizontal grouped bar chart represents data with groups of horizontal bars placed side
          by side. Each group corresponds to a main category, while the bars within it represent
          subcategories or variables. This format is especially useful for comparing multiple data
          series, highlighting differences, and accommodating long category labels for better
          readability.
        </p>
      </div>
      <div className="usage">
        <h2 className="title" id="usage">
          Usage
        </h2>
        <p className="subtitle">Horizontal grouped bar charts are ideal for:</p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Comparing Multiple Variables Across Categories:</strong> They are effective for
            comparing different data series within each category, such as comparing revenue,
            expenses, and profit across multiple regions or departments.
          </li>
          <li>
            <strong>Displaying Long Category Labels:</strong> When categories have long names,
            horizontal grouped bar charts offer better readability by preventing label overlap,
            making them suitable for datasets with detailed or lengthy labels.
          </li>
          <li>
            <strong>Visualizing Rank or Performance Across Categories:</strong> They are perfect for
            comparing performance across various groups or categories, such as evaluating different
            products or teams, where the ranking and differences in magnitude are clear.
          </li>
        </ul>
      </div>
      <div className="limitations">
        <h2 className="title" id="limitations">
          Limitations
        </h2>
        <p className="subtitle">
          Horizontal grouped bar charts are a powerful tool for comparing data across categories,
          but they come with specific limitations that should be considered:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Difficulty with Long Category Names:</strong> When category names are long, they
            may not fit properly along the vertical axis, causing labels to overlap or become
            unreadable.
          </li>
          <li>
            <strong>Challenges with Small Data Differences:</strong> Horizontal grouped bar charts
            can make it difficult to discern small differences between bars, especially when the
            values are close together, which can lead to misinterpretations.
          </li>
          <li>
            <strong>Cluttered Appearance with Many Groups:</strong> With a large number of groups or
            categories, horizontal grouped bar charts can become cluttered, making it hard to
            extract meaningful insights and reducing the overall effectiveness of the visualization.
          </li>
        </ul>
      </div>
      <div className="colors">
        <h2 className="title" id="colors">
          Colors
        </h2>
        <p>
          Color selection is crucial in horizontal grouped bar charts to enhance readability and
          distinguish between groups and subcategories. Assigning distinct colors to each group and
          varying shades or patterns for subcategories ensures clarity in comparisons. High-contrast
          palettes prevent overlap confusion, while subtle hues can emphasize less critical data
          without overwhelming the chart. Well-chosen colors guide the viewer’s attention and
          simplify the interpretation of complex datasets.
        </p>
        <NotificationStyled
          icon={<InfoCircle />}
          title="For in-depth information on this topic, please refer to the Colors page."
          color="blue"
          className="info-notification"
        />
        <UseCasePalettes palettes={['qualitative']} />
      </div>

      <div className="challenges">
        <h2 className="title">Challenges</h2>
        <p>
          Horizontal grouped bar charts are effective in comparing multiple groups, but they come
          with certain challenges that should be addressed:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Space Constraints for Category Labels:</strong> In horizontal grouped bar
            charts, the length of category labels can limit space, making it harder to fit long
            descriptions without crowding or overlapping.
          </li>
          <li>
            <strong>Visual Complexity with Many Groups:</strong> As the number of groups increases,
            the chart can become visually complex and harder to interpret, with bars becoming
            thinner and more difficult to differentiate.
          </li>
          <li>
            <strong>Difficulty in Comparing Bars Across Groups:</strong> Horizontal grouped bar
            charts can pose challenges when trying to compare bars across different groups,
            especially when the values are close in magnitude, leading to potential
            misinterpretations.
          </li>
        </ul>
      </div>
    </div>
    <div>
      <StickyNav title="Contents" />
    </div>
  </div>
);

const StyledUseCaseTab = styled(UseCaseTab)`
  display: flex;
  gap: 2.5rem;
  justify-content: space-between;

  > .tab-content {
    width: 100%;
    .usage,
    .definition,
    .limitations,
    .challenges {
      max-width: 700px;
    }
    > .colors {
      > .info-notification {
        margin-top: 24px;
        margin-bottom: 40px;
      }

      > .palette {
        > .palette-title {
          color: ${({ theme }) => theme.neutralText};
          font-size: ${({ theme }) => theme.fontSizes.base};
          line-height: ${({ theme }) => theme.lineHeights.base};
          margin-bottom: 4px;
        }
      }

      .color-palette {
        margin-top: 12px;
      }
    }

    > .challenges {
      > .ordered-list-badge-num {
        margin-top: 16px;
      }
    }

    .title {
      color: ${({ theme }) => theme.neutralText};
      font-weight: 600;
      font-size: ${({ theme }) => theme.fontSizes.lg};
      line-height: ${({ theme }) => theme.lineHeights.lg};
      margin-bottom: 8px;
      margin-top: 40px;
    }

    p {
      color: ${({ theme }) => theme.neutralTextWeak};
      font-size: ${({ theme }) => theme.fontSizes.sm};
      line-height: ${({ theme }) => theme.lineHeights.sm};
      font-weight: 400;
      max-width: 700px;
    }

    .subtitle {
      margin-bottom: 16px;
    }

    // styles for list with badge
    .ordered-list-badge-num {
      font-size: 0.875rem;
      line-height: 1.4285714286;
      margin-left: 29px;
      counter-reset: num;
      list-style: none;
      color: ${({ theme }) => theme.neutralTextWeak};

      li {
        position: relative;
        margin-bottom: 16px;

        &::before {
          content: counter(num);
          counter-increment: num;
          position: absolute;
          left: -1.75rem;
          top: 0;
          width: 1.25rem;
          height: 1.25rem;
          border-radius: 0.1875rem;
          text-align: center;
          font-size: 0.6875rem;
          line-height: 1.8;
          color: ${({ theme }) => theme.primaryText};
          background-color: ${({ theme }) => theme.primaryBackgroundActive};
        }
      }
    }
  }
`;

export default StyledUseCaseTab;
